import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LifeBuoy, AlertTriangle, Pencil, Check, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABELS = {
  nomina_payments: 'Nómina',
  sales: 'Ventas',
  'sales.items': 'Ventas',
  appointments: 'Citas',
  commissions: 'Comisiones',
  appointment_audit_log: 'Historial de citas',
  sale_audits: 'Auditoría de ventas',
  sale_edit_requests: 'Solicitudes de edición',
  fingerprint_enrollments: 'Checador',
  attendance_records: 'Asistencia',
  perfil: 'Editado manualmente',
};

function RecoveredRow({ r, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(r.display_name || '');

  const save = async () => {
    const v = name.trim();
    if (!v) return toast.error('Escribe un nombre');
    try {
      await axios.put(`${API_URL}/barbers/${r.id}`, { display_name: v });
      toast.success(`Nombre actualizado a "${v}"`);
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    }
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2 text-sm" data-testid={`recovered-row-${r.id}`}>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()}
              className="h-8 max-w-xs" data-testid={`recovered-name-input-${r.id}`} />
            <Button size="icon" className="h-8 w-8" onClick={save} data-testid={`recovered-name-save-${r.id}`}><Check className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(false)}><X className="w-4 h-4" /></Button>
          </div>
        ) : (
          <p className="font-medium text-slate-900 truncate flex items-center gap-2">
            {!r.name_found && <AlertTriangle className="w-4 h-4 text-amber-600" />}
            {r.display_name}
            <button type="button" onClick={() => setEditing(true)} className="text-slate-400 hover:text-slate-700" data-testid={`recovered-name-edit-${r.id}`}>
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </p>
        )}
        <p className="text-xs text-slate-500">
          {r.name_found ? `Nombre: ${SOURCE_LABELS[r.name_source] || r.name_source || '—'}` : 'Nombre no encontrado en ningún registro'}
          {r.first_date && ` · Citas del ${r.first_date} al ${r.last_date}`}
        </p>
      </div>
      <Badge variant="outline" className="text-xs" data-testid={`recovered-today-${r.id}`}>Hoy: {r.appointments_today}</Badge>
      <Badge variant="outline" className="text-xs" data-testid={`recovered-total-${r.id}`}>Citas: {r.appointments_total}</Badge>
      <Badge className="bg-emerald-600 text-white text-xs" data-testid={`recovered-reassigned-${r.id}`}>Reasignadas: {r.appointments_reassigned}</Badge>
    </div>
  );
}

export const RecoveredEmployeesCard = ({ refreshKey, onChanged }) => {
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API_URL}/admin/recovered-employees`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!rows.length) return null;

  return (
    <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/60" data-testid="recovered-employees-card">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-emerald-200">
        <LifeBuoy className="w-4 h-4 text-emerald-700" />
        <p className="font-semibold text-emerald-900 text-sm">Empleados recuperados ({rows.length})</p>
        <span className="text-xs text-emerald-700 ml-auto">Toca el lápiz para escribir el nombre correcto</span>
      </div>
      <div className="divide-y divide-emerald-100">
        {rows.map((r) => <RecoveredRow key={r.id} r={r} onSaved={() => { load(); onChanged?.(); }} />)}
      </div>
    </div>
  );
};
